import { useEffect, useState } from "react";
import { Menu, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, REGISTER_URL, TELEGRAM_URL, scrollToSection } from "@/data/site";

const NAV_LINKS = [
  { label: "Cara Kerja", target: "#cara-kerja" },
  { label: "Hadiah Unggulan", target: "#showcase" },
  { label: "Semua Hadiah", target: "#semua-hadiah" },
  { label: "Rebate", target: "#rebate" },
];

export const LogoMark = ({ className = "h-9 w-9" }) => (
  <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
    <defs>
      <linearGradient id="lg" x1="14" y1="16" x2="50" y2="36" gradientUnits="userSpaceOnUse">
        <stop stopColor="#F0B429" />
        <stop offset="1" stopColor="#FFE08A" />
      </linearGradient>
    </defs>
    <rect width="64" height="64" rx="14" fill="#0D111A" />
    <rect x="1.5" y="1.5" width="61" height="61" rx="12.5" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="1.5" />
    <path d="M15 38 L32 21 L49 38" stroke="url(#lg)" strokeWidth="7" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 51 L32 34 L49 51" stroke="#00F2B6" strokeWidth="7" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.55" />
  </svg>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    scrollToSection(target);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-white/10 bg-[#07090E]/85 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <a
          href="#"
          data-testid="navbar-brand-link"
          onClick={(e) => {
            e.preventDefault();
            window.__lenis
              ? window.__lenis.scrollTo(0)
              : window.scrollTo({ top: 0, behavior: "smooth" });
            setOpen(false);
          }}
          className="flex items-center gap-2.5"
        >
          <LogoMark />
          <span className="font-display text-base font-bold uppercase tracking-wide text-white sm:text-lg">
            Trade <span className="text-[#F0B429]">&</span> Claim
            <span className="ml-1.5 hidden font-sans text-xs font-medium text-zinc-400 sm:inline">
              × Self Rebate
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-7 md:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.target}
              data-testid={`navbar-nav-${l.target.slice(1)}`}
              onClick={() => go(l.target)}
              className="text-sm font-medium text-zinc-400 transition-colors hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            asChild
            data-testid="navbar-cta-telegram-button"
            className="hidden glass h-10 rounded-full px-4 text-white hover:border-teal-400/50 sm:inline-flex"
          >
            <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" onClick={() => window.fbq && window.fbq("trackCustom", "TelegramClick")}>
              <Send className="h-4 w-4 text-[#00F2B6]" />
              Tanya Admin
            </a>
          </Button>
          <Button
            asChild
            data-testid="navbar-cta-daftar-button"
            className="hidden h-10 rounded-full bg-[#F0B429] px-5 text-sm font-bold text-[#07090E] hover:bg-[#D9A019] glow-gold sm:inline-flex"
          >
            <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
              Daftar Sekarang
            </a>
          </Button>
          <button
            data-testid="navbar-menu-toggle"
            aria-label="Buka menu"
            onClick={() => setOpen(!open)}
            className="glass flex h-10 w-10 items-center justify-center rounded-full text-white md:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="border-t border-white/10 bg-[#07090E]/95 px-4 pb-6 pt-3 backdrop-blur-xl md:hidden"
        >
          {NAV_LINKS.map((l) => (
            <button
              key={l.target}
              onClick={() => go(l.target)}
              className="block w-full border-b border-white/5 py-3.5 text-left text-base font-medium text-zinc-300"
            >
              {l.label}
            </button>
          ))}
          <Button
            asChild
            data-testid="navbar-mobile-daftar-button"
            className="mt-4 h-11 w-full rounded-full bg-[#F0B429] font-bold text-[#07090E] hover:bg-[#D9A019]"
          >
            <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer">
              Daftar Sekarang
            </a>
          </Button>
        </div>
      )}
    </header>
  );
}
